import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchJournal } from "@/lib/api";
import Seo from "@/components/seo/Seo";

const CATEGORIES = ["All", "Ritual", "Recovery", "Movement", "Sensory", "Ancient Wisdom"];

export default function Journal() {
  const [articles, setArticles] = useState([]);
  const [active, setActive] = useState("All");

  useEffect(() => {
    fetchJournal().then(setArticles).catch(() => {});
  }, []);

  const filtered = active === "All" ? articles : articles.filter((a) => a.category === active);

  return (
    <div data-testid="journal-page" className="pt-32 md:pt-40">
      <Seo
        path="/journal"
        title="The Journal"
        description="Field notes, essays, and quiet provocations on ritual recovery, sensory living, movement, and ancient wisdom."
      />
      <section className="px-6 md:px-12 lg:px-16 pb-12">
        <p className="overline mb-6">The Journal</p>
        <h1 className="font-display text-5xl md:text-7xl lg:text-[8rem] font-light leading-[0.92] tracking-[-0.03em] text-ink">
          Field notes,<br />
          <em className="italic text-moss">essays,</em><br />
          quiet provocations.
        </h1>
        <p className="mt-10 max-w-xl text-charcoal text-lg md:text-xl font-light leading-relaxed">
          Long-form writing on ritual recovery, sensory living, movement, and the
          evidence-based return to ancient ways of living well.
        </p>
      </section>

      {/* Filter strip */}
      <section className="px-6 md:px-12 lg:px-16 py-6 border-t border-hairline mt-12">
        <div className="flex items-center gap-2 md:gap-6 overflow-x-auto no-scrollbar">
          <p className="overline text-stone mr-4 hidden md:block">Browse —</p>
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              data-testid={`filter-${c.toLowerCase().replace(/\s+/g, '-')}`}
              className={`text-[0.72rem] tracking-[0.22em] uppercase font-medium py-2 px-3 transition-colors whitespace-nowrap ${
                active === c ? "bg-ink text-bone" : "text-charcoal hover:text-ink"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* Article list */}
      <section className="px-6 md:px-12 lg:px-16 py-12">
        <ul>
          {filtered.map((a, idx) => (
            <li key={a.slug} className="border-t border-hairline group">
              <Link
                to={`/journal/${a.slug}`}
                data-testid={`article-link-${a.slug}`}
                className="grid grid-cols-12 gap-4 md:gap-8 py-8 md:py-12 items-start transition-colors hover:bg-clay/40 -mx-3 px-3"
              >
                <span className="col-span-1 text-xs md:text-sm text-stone font-mono mt-2">
                  {String(idx + 1).padStart(2, "0")}
                </span>

                <div className="col-span-11 md:col-span-5">
                  <p className="overline mb-3">{a.category} · {a.read_time}</p>
                  <h2 className="font-display text-2xl md:text-4xl lg:text-5xl text-ink leading-tight tracking-tight group-hover:italic transition-all">
                    {a.title}
                  </h2>
                  <p className="font-display italic text-lg md:text-xl text-charcoal mt-3 leading-snug">
                    {a.subtitle}
                  </p>
                </div>

                <div className="col-span-12 md:col-span-3 md:col-start-7 mt-4 md:mt-0">
                  <p className="text-sm md:text-base text-charcoal/80 font-light leading-relaxed">
                    {a.excerpt}
                  </p>
                  <p className="overline mt-4 text-stone">— {a.author}</p>
                </div>

                <div className="hidden md:block col-span-3 col-start-10 img-zoom aspect-[5/6] overflow-hidden">
                  <img src={a.cover_image} alt={a.title} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" />
                </div>
              </Link>
            </li>
          ))}
          <li className="border-t border-hairline" />
        </ul>
      </section>
    </div>
  );
}
